// App-wide constants

export const APP_NAME = 'Golf Pickem'

// API polling intervals (in milliseconds)
export const POLLING_INTERVALS = {
  ACTIVE_TOURNAMENT: 30 * 1000, // 30 seconds
  INACTIVE_TOURNAMENT: 5 * 60 * 1000, // 5 minutes
  BACKGROUND_TAB: 2 * 60 * 1000, // 2 minutes
}

// Query keys
export const QUERY_KEYS = {
  USER_ME: ['user', 'me'],
  USER: (userId: string) => ['user', userId],
  TOURNAMENTS: (leagueId?: string, status?: string) => ['tournaments', leagueId, status],
  TOURNAMENT: (tourneyId: string) => ['tournament', tourneyId],
  LINEUP: (tourneyId: string) => ['lineup', tourneyId],
  LEAGUE: (leagueId: string) => ['league', leagueId],
  LEAGUE_LEADERBOARD: (leagueId: string) => ['league', leagueId, 'leaderboard'],
  SIDEPOT: (sidePotId: string) => ['sidepot', sidePotId],
  RECENT_USERS: ['admin', 'recent-users'],
}

// Local storage keys
export const STORAGE_KEYS = {
  LINEUP_DRAFT: (tourneyId: string) => `lineup-draft-${tourneyId}`,
  THEME_PREFERENCE: 'theme-preference',
  SIDEBAR_COLLAPSED: 'sidebar-collapsed',
}

// Tournament statuses
export const TOURNAMENT_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
} as const

// Lineup statuses
export const LINEUP_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const

// Email send-to options
export const EMAIL_SEND_TO = {
  ALL_USERS: 'allUsers',
  REGISTERED: 'registeredUsers',
  NOT_REGISTERED: 'notRegistered',
  CUSTOM: 'custom',
} as const

// Golfer tiers
export const GOLFER_TIERS = {
  A: 'A',
  B: 'B',
  C: 'C',
} as const

// Tier limits for lineup
export const TIER_LIMITS = {
  A: 2, // Max 2 from tier A
  B: 2, // Max 2 from tier B
  // No limit on tier C
}

// Max picks per lineup
export const MAX_LINEUP_PICKS = 7

// Default league ID (if only one league)
export const DEFAULT_LEAGUE_ID = 1

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  DISPLAY_WITH_TIME: 'MMM d, yyyy h:mm a',
  API: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  SHORT: 'MMM d',
  LONG: 'MMMM d, yyyy',
}

// Breakpoints (matches Tailwind)
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
}

// Toast durations (in milliseconds)
export const TOAST_DURATION = {
  SHORT: 2000,
  DEFAULT: 4000,
  LONG: 6000,
}

// Error messages
export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You must be logged in to access this page.',
  FORBIDDEN: 'You do not have permission to access this page.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION: 'Please check your input and try again.',
}

// Success messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Welcome back!',
  SIGNUP: 'Account created successfully! Please check your email to verify.',
  LOGOUT: 'Logged out successfully.',
  LINEUP_SUBMITTED: 'Lineup submitted successfully!',
  PROFILE_UPDATED: 'Profile updated successfully.',
  PASSWORD_RESET: 'Password reset successfully.',
  EMAIL_SENT: 'Email sent successfully.',
}

// Route paths
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  SIGNUP_COMPLETE: '/signup/complete',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_EMAIL: '/verify-email',
  TOURNAMENTS: '/tournaments',
  TOURNAMENT_DETAIL: (id: string) => `/tournaments/${id}`,
  LINEUP_PICKER: (id: string) => `/tournaments/${id}/pick`,
  MY_LINEUPS: '/lineups',
  CAREER_LEADERBOARD: '/career-leaderboard',
  PROFILE: '/profile',
  PROFILE_EDIT: '/profile/edit',
  USER_DETAIL: (id: string) => `/users/${id}`,
  LEAGUE_MEMBERS: (id: string) => `/leagues/${id}/members`,
  LEAGUE_LEADERBOARD: (id: string) => `/leagues/${id}/leaderboard`,
  SIDEPOT: (id: string) => `/sidepots/${id}`,
  ADMIN_DASHBOARD: '/admin',
  ADMIN_TOURNAMENT_CREATE: '/admin/tournaments/new',
  ADMIN_TOURNAMENT_EDIT: (id: string) => `/admin/tournaments/${id}/edit`,
  ADMIN_APPROVE_LINEUPS: (id: string) => `/admin/tournaments/${id}/approve`,
  ADMIN_EMAIL: '/admin/email',
  ADMIN_USERS: '/admin/users',
} as const
